<script setup lang="ts">
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import EventHeroPanel from '@/pages/organizer/events/settings/EventHeroPanel.vue'
import EventSignaturePanel from '@/pages/organizer/events/settings/EventSignaturePanel.vue'

const { t } = useI18n()
const route = useRoute()
const eventSlug = route.params['eventSlug'] as string

const { event, updateEvent } = useEvents()

const youtubeUrl = ref<string>('')
const youtubeUrlError = ref<string | null>(null)
let youtubeDebounceTimer: ReturnType<typeof setTimeout> | null = null

watch(
  () => event.value?.youtube_url,
  (val) => {
    youtubeUrl.value = val ?? ''
  },
  { immediate: true },
)

function onYoutubeUrlInput(): void {
  youtubeUrlError.value = null
  if (youtubeDebounceTimer) clearTimeout(youtubeDebounceTimer)
  youtubeDebounceTimer = setTimeout(() => void saveYoutubeUrl(), 800)
}

async function saveYoutubeUrl(): Promise<void> {
  if (!event.value) return
  const val = youtubeUrl.value.trim() || null
  try {
    await updateEvent(eventSlug, { name: event.value.name, youtube_url: val })
    youtubeUrlError.value = null
  } catch (e: unknown) {
    const err = e as { response?: { data?: { errors?: Record<string, string[]> } } }
    youtubeUrlError.value = err.response?.data?.errors?.['youtube_url']?.[0] ?? null
  }
}
</script>

<template>
  <div class="space-y-6">
    <EventHeroPanel />
    <EventSignaturePanel />

    <!-- YouTube URL -->
    <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
      <h2 class="text-lg font-semibold text-white mb-1">
        {{ t('events.public_site.youtube_url_label') }}
      </h2>
      <p class="text-sm text-slate-400 mb-4">
        {{ t('events.public_site.youtube_url_description') }}
      </p>
      <div class="relative">
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg class="w-4 h-4 text-red-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
          </svg>
        </div>
        <input
          v-model="youtubeUrl"
          type="url"
          :placeholder="t('events.public_site.youtube_url_placeholder')"
          class="block w-full rounded-lg border bg-slate-900/60 pl-9 pr-4 py-2.5 text-sm text-white placeholder-slate-500 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
          :class="youtubeUrlError ? 'border-red-500' : 'border-slate-600 focus:border-emerald-500'"
          @input="onYoutubeUrlInput"
        />
      </div>
      <p v-if="youtubeUrlError" class="mt-2 text-sm text-red-400">{{ youtubeUrlError }}</p>
      <p v-else class="mt-2 text-xs text-slate-500">{{ t('events.public_site.youtube_url_hint') }}</p>
    </div>
  </div>
</template>
